import { buildTree } from './tree';

const ELIM_FORMATS = ['single_elimination', 'double_elimination', 'hybrid', 'consolation'];

export const isEliminationFormat = (format, playoffs) => ELIM_FORMATS.includes(format) || !!playoffs?.enabled;

export function eliminationMatches(schedule, format) {
  const elim = ELIM_FORMATS.includes(format);
  return (schedule || []).filter((m) => {
    const s = m.stage || '';
    return s.includes('PO_') || (elim && !s.includes('RR'));
  });
}

const rankOf = (ms) => {
  const rs = [...new Set(ms.map((m) => m.round ?? 0))].sort((a, b) => a - b);
  return new Map(rs.map((r, i) => [r, i]));
};

function pickRoot(pool, kinds) {
  const top = [...new Set(pool.map((m) => m.round ?? 0))].sort((a, b) => b - a).slice(0, 2);
  let best = null;
  pool.filter((m) => top.includes(m.round ?? 0)).forEach((m) => {
    const t = buildTree(m, pool, kinds);
    const better = !best || t.used.size > best.used.size
      || (t.used.size === best.used.size && (m.round ?? 0) > (best.tree.match.round ?? 0));
    if (better) best = t;
  });
  return best;
}

function singleModel(ms) {
  const { tree, used } = pickRoot(ms, ['W']);
  const rank = rankOf(ms.filter((m) => used.has(m.id)));
  return {
    kind: 'single', matches: ms, root: tree, rank, rounds: rank.size,
    extras: ms.filter((m) => !used.has(m.id)),
    canMirror: !!(tree.kids[0] && tree.kids[1]),
  };
}

function section(pool) {
  if (!pool.length) return null;
  const { tree, used } = pickRoot(pool, ['W']);
  return { root: tree, used, rank: rankOf(pool.filter((m) => used.has(m.id))) };
}

function doubleModel(ms) {
  const wb = section(ms.filter((m) => m.stage === 'WB' || m.stage === 'PO_WB'));
  const lb = section(ms.filter((m) => m.stage === 'LB'));
  const gf = ms.find((m) => m.stage === 'GF') || null;
  const used = new Set([...wb.used, ...(lb ? lb.used : []), ...(gf ? [gf.id] : [])]);
  return { kind: 'double', matches: ms, wb, lb, gf, extras: ms.filter((m) => !used.has(m.id)) };
}

const KNOWN_CONSOLATION = ['QF', 'WB_SF', 'CB_SF', 'CHAMP', '5TH', '3RD', '7TH'];

function consolationModel(ms) {
  const byPos = (a, b) => (a.bracket_position || 0) - (b.bracket_position || 0);
  const list = (s) => ms.filter((m) => m.stage === s).sort(byPos);
  const one = (s) => ms.find((m) => m.stage === s) || null;
  return {
    kind: 'consolation', matches: ms,
    qf: list('QF'), wbSf: list('WB_SF'), cbSf: list('CB_SF'),
    champ: one('CHAMP'), fifth: one('5TH'), third: one('3RD'), seventh: one('7TH'),
    extras: ms.filter((m) => !KNOWN_CONSOLATION.includes(m.stage)),
  };
}

export function buildBracketModel({ format, schedule }) {
  const ms = eliminationMatches(schedule, format);
  if (!ms.length) return null;
  if (format === 'consolation') return consolationModel(ms);
  if (ms.some((m) => m.stage === 'WB' || m.stage === 'PO_WB')) return doubleModel(ms);
  return singleModel(ms);
}

export function describeEvent(ev) {
  const date = ev?.start_date
    ? new Date(`${ev.start_date}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;
  const fmt = (ev?.format || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const teams = ev?.teams?.length ? `${ev.teams.length} Teams` : null;
  return [date, teams, fmt, ev?.venue].filter(Boolean).join('  ·  ');
}
